<template>
    <b-card no-body>
        <b-card-header>
            <i class="icon-list"></i>
            <strong>{{ $t('indicators.indicators') }}</strong>
        </b-card-header>
        <b-card-body>
            <b-container fluid>
                <b-row>
                    <b-col>
                        <div class="mb-3 text-right">
                            <b-button size="sm" variant="primary" @click="newKpi()">
                                {{ $t('indicators.newIndicator') }}
                            </b-button>
                        </div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="mb-1" sm="12" xl="6">
                        <c-periods
                            :state="null"
                            :value.sync="filtered.periods"
                            :label="$t('targets.showInfoBySelectedPeriods')"
                            applyScopeNotArchived
                        />
                    </b-col>
                    <b-col class="mb-1" sm="12" xl="6">
                        <c-sections
                            :state="null"
                            :value.sync="filtered.sections"
                            :label="$t('form.label.departments')"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col class="mb-1" sm="12" xl="6">
                        <b-form-group>
                            <b-form-input
                                :placeholder="$t('form.placeholder.searchByKpiOwner')"
                                v-model="filtered.name"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col class="mb-1" sm="12" xl="5">
                        <b-form-group>
                            <b-form-input
                                id="search-by-title"
                                v-model="filtered.searchText"
                                :placeholder="$t('form.placeholder.searchByKpiTitle')"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col class="mb-1" sm="2" xl="1">
                        <b-form-group class="text-right">
                            <b-form-select
                                v-model="pagination.perPage"
                                :options="pageOptions"
                                @change="onChangePerPageHandler"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-table
                    hover
                    striped
                    bordered
                    small
                    :items="kpis"
                    :fields="fields"
                    :busy="isLoading"
                    :show-empty="true"
                    :empty-text="$t('targets.emptyTable')"
                >
                    <template #thead-top>
                        <tr>
                            <th colspan="2" class="text-center align-middle">
                                {{ $t('roles.user') }}
                            </th>
                            <th colspan="5" class="text-center align-middle">
                                {{ $t('indicators.indicators') }}
                            </th>
                        </tr>
                    </template>
                    <template #cell(title)="{ item }">
                        <b-link :to="`/v2/target/indicators/view/${item.id}`">
                            {{ item.title }}
                        </b-link>
                    </template>
                    <template #cell(description)="{ item }">
                        <p v-html="item.description"></p>
                    </template>
                    <template #cell(picture)="{ item }">
                        <b-link :to="`/v2/profile/${item.user_id}`">
                            <avatar
                                class="d-inline-block"
                                :src="item.user.picture"
                                :username="item.user.name"
                                :size="35"
                            />
                        </b-link>
                    </template>
                    <template #cell(owner)="{ item }">
                        <router-link :to="`/v2/profile/${item.user.id}`">
                            <strong>{{ item.user.name }}</strong>
                        </router-link>
                    </template>
                    <template #cell(progress)="{ item }">
                        <span v-if="item.progress"> {{ formatNumber(item.progress) }}% </span>
                        <span v-else>
                            {{ '-' }}
                        </span>
                        <div v-if="item.progress" class="progress-bar-padding">
                            <progress-bar :value="item.progress" :color="item.progress_colors" />
                        </div>
                        <div v-else>
                            {{ ' ' }}
                        </div>
                    </template>
                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle"></b-spinner>
                        </div>
                    </template>
                </b-table>
                <div class="d-flex justify-content-center">
                    <b-pagination
                        class="mb-0"
                        v-show="!isLoading"
                        :total-rows="pagination.total"
                        :per-page="pagination.perPage"
                        v-model="pagination.currentPage"
                        @change="onChangePaginationHandler"
                    />
                </div>
            </b-container>
        </b-card-body>
    </b-card>
</template>

<script>
import Swal from 'sweetalert2'
import i18n from '@/i18n'
import api from '@/services/api'
import { getUserPermissions } from '@/services/user-permissions'
import { formatNumber } from '@/lib/utils'
import { Periods as CPeriods, SectionsWithoutLevel as CSections } from '@/views/components/fields'
import Avatar from 'vue-avatar'
import mapperKpiFilters from './mappers/mapperKpiFilters'
import { getSearchString } from '@/lib/api'
import { debounce } from 'lodash'
import ProgressBar from '@/views/components/progress-bar/ProgressBar'

export default {
    name: 'List',
    components: {
        Avatar,
        CPeriods,
        CSections,
        ProgressBar,
    },
    data() {
        return {
            permissions: {},
            kpis: [],
            isLoading: true,
            fields: [
                {
                    key: 'picture',
                    label: i18n.t('targets.picture'),
                    class: 'text-center align-middle',
                },
                {
                    key: 'owner',
                    label: i18n.t('targets.name'),
                    class: 'text-center align-middle',
                },
                {
                    key: 'title',
                    label: i18n.t('targets.title'),
                    class: 'text-center align-middle',
                },
                {
                    key: 'description',
                    label: i18n.t('profile.table.description'),
                    class: 'text-center align-middle',
                },
                {
                    key: 'progress',
                    label: i18n.t('targets.performance'),
                    class: 'text-center align-middle',
                },
            ],
            pageOptions: ['5', '10', '20', '30', '50'],
            pagination: {
                total: null,
                perPage: 10,
                currentPage: 1,
            },
            filtered: {
                periods: [],
                sections: [],
                searchText: '',
                name: '',
            },
        }
    },
    created() {
        this.permissions = getUserPermissions()
    },
    mounted() {
        this.isAllowedToShowKpis()
        this.getKpisFromApi()
    },
    methods: {
        formatNumber(number) {
            return formatNumber(number)
        },
        getKpisFromApi(page) {
            this.isLoading = true
            return api
                .get('/kpi', {
                    params: {
                        page: page,
                        per_page: this.pagination.perPage,
                        with: 'user',
                        search: getSearchString(mapperKpiFilters(this.filtered)),
                        and: 'yes',
                    },
                })
                .then((resp) => {
                    const { data, current_page, per_page, total } = resp.data.data
                    this.kpis = data
                    this.pagination = {
                        total,
                        perPage: per_page,
                        currentPage: current_page,
                    }
                })
                .catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        isAllowedToShowKpis() {
            if (!this.permissions.modules.has_kpi) {
                Swal({
                    type: 'warning',
                    title: i18n.t('notification.permissions.showDenied', {
                        item: i18n.t('indicators.indicators'),
                    }),
                })
            }
        },
        newKpi() {
            if (!this.permissions.modules.has_kpi) {
                Swal({
                    type: 'warning',
                    title: i18n.t('notification.permissions.createDenied', {
                        item: i18n.t('indicators.indicator'),
                    }),
                })
            } else {
                this.$router.push({
                    path: '/v2/target/indicators/new',
                })
            }
        },
        onChangePaginationHandler(currentPage) {
            this.getKpisFromApi(currentPage)
        },
        onChangePerPageHandler() {
            this.getKpisFromApi()
        },
    },
    watch: {
        filtered: {
            handler: debounce(function () {
                return this.getKpisFromApi()
            }, 500),
            deep: true,
        },
    },
}
</script>
<style lang="scss" scope>
.progress-bar-padding {
    padding-right: 20px;
    padding-left: 20px;
}
</style>
